import type { CatalogItem, CatalogItemUnit } from '@matix/types';

/**
 * Price-tier resolution — what an item costs on a given invoice source.
 *
 * An invoice source (Mstr_Pos_InvoiceSource_Setup) names one of the four
 * `Sale_priceN` columns on `Inv_Items_Units`. Every item the catalog sync
 * returns already carries all four, so switching source is a local
 * recalculation: no refetch, works offline, instant.
 *
 * ── THE FALLBACK IS THE WHOLE POINT OF THIS FILE ──────────────────────────
 *
 * The legacy POS reads the tier column straight out of the item and assigns it
 * with no guard (`ApplyPriceListInCart`, order_events_definitions.js:3453):
 *
 *     else if (priceListNo == 2) { itemPrice = AddeditemObj[0].UnitPrice2; }
 *
 * On GL_WEB_Pizzata that is a live revenue hole. Of 635 POS-sellable unit rows,
 * 273 carry a tier-1 price and only 111 carry tier 2 — so under the "سعر طلبات"
 * source, 162 items (every coffee, tea, juice and side) would ring up at 0.00.
 *
 * We fall back to tier 1 instead. A coffee then sells at the dine-in price on a
 * delivery ticket, which is wrong by the markup (tier 2 averages 113% of tier 1
 * on that tenant) but is never free. The real fix is the tenant filling in
 * Sale_price2, and the admin Invoice Sources screen counts how many are still
 * missing so somebody can see the gap rather than discover it in a Z report.
 *
 * A tier price of 0 is treated as "not set", not as "free". The legacy back
 * office writes 0 into these columns for unpriced tiers rather than NULL —
 * that is exactly what those 162 rows hold — so honouring a 0 would reproduce
 * the bug the fallback exists to avoid. An item with no tier-1 price either
 * still resolves to 0, and the till already refuses to sell those.
 */

/**
 * The unit an item is sold by, matching the server's own resolution order
 * (`resolveEffectiveUnitId` in pos-api, and the catalog's displayed price).
 *
 * The POS flag beats `selling_unit_id`: the latter is the BACK OFFICE's default
 * unit, not a promise the till sells in it. On GL_WEB_binqinwan item 9 the
 * selling unit is a non-POS `box` row (FinalConv 42875, 9475.375 KWD) while the
 * only POS row is `pcs` at 0.221 — preferring it showed the box price on a pcs
 * tile and deducted 42,875 pieces per can sold.
 *
 * A non-POS selling unit is still honoured when the item has NO POS rows at
 * all, so such an item keeps a tile and a price instead of vanishing.
 */
export function sellingUnitOf(item: CatalogItem): CatalogItemUnit | undefined {
  const sellingId = item.selling_unit_id;
  return (
    item.units.find((u) => sellingId != null && u.unit_id === sellingId && u.pos_enabled) ??
    item.units.find((u) => u.pos_enabled) ??
    item.units.find((u) => sellingId != null && u.unit_id === sellingId) ??
    item.units[0]
  );
}

/** Read one tier off a unit. Returns null for absent/zero — i.e. "not priced". */
function tierOf(unit: CatalogItemUnit | undefined, tier: 1 | 2 | 3 | 4): number | null {
  if (!unit) return null;
  const raw =
    tier === 1 ? unit.sale_price1
    : tier === 2 ? unit.sale_price2
    : tier === 3 ? unit.sale_price3
    : unit.sale_price4;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The units the till sells this item in — one catalog tile each, the way the
 * legacy POS laid them out ("Piece 0.150" beside "CTN 2.750"). POS-enabled
 * rows only; an item with none falls back to its selling unit so it still
 * gets one tile. Empty only for an item with no unit rows at all.
 */
export function posUnitsOf(item: CatalogItem): CatalogItemUnit[] {
  const pos = item.units.filter((u) => u.pos_enabled);
  if (pos.length > 0) return pos;
  const selling = sellingUnitOf(item);
  return selling ? [selling] : [];
}

/** More than one tile: the unit is named on the tile, the line and the slip. */
export function isMultiUnit(item: CatalogItem): boolean {
  return posUnitsOf(item).length > 1;
}

/**
 * What this item costs under the given source tier, in the given unit.
 *
 * `unit` omitted means the selling unit, and `tier` null (no source selected,
 * or the tenant has no sources) then returns the server-resolved
 * `selling_price`, so every tenant that does not use price lists behaves
 * exactly as it did before this feature existed. A named unit prices off its
 * own row: tier 1 is `sale_price1`; another tier falls back to that row's
 * tier 1 rather than selling for nothing.
 */
export function priceForTier(
  item: CatalogItem,
  tier: 1 | 2 | 3 | 4 | null,
  unit?: CatalogItemUnit | null,
): number {
  if (!unit) {
    if (tier == null || tier === 1) return item.selling_price ?? 0;
    const selling = sellingUnitOf(item);
    const tiered = tierOf(selling, tier);
    if (tiered != null) return tiered;
    // Not priced on this tier — fall back rather than sell it for nothing.
    return tierOf(selling, 1) ?? item.selling_price ?? 0;
  }
  const t = tier == null ? 1 : tier;
  const tiered = tierOf(unit, t);
  if (tiered != null) return tiered;
  if (t !== 1) {
    const base = tierOf(unit, 1);
    if (base != null) return base;
  }
  // A unit with no price of its own: the selling unit's resolution, which is
  // what the tile showed before units were split out. Compared against the
  // RESOLVED selling unit, not the raw selling_unit_id — `selling_price` is the
  // resolved unit's sale_price1, so keying off the raw column would hand a
  // non-POS unit's price to a POS unit (or hand out 0 for the unit that earned
  // the price).
  return unit.unit_id === sellingUnitOf(item)?.unit_id ? item.selling_price ?? 0 : 0;
}

/**
 * Did this item fall back? Used only to mark the tile, so a cashier can see
 * that a price is the dine-in one rather than silently assuming the delivery
 * markup was applied.
 */
export function isFallbackPrice(
  item: CatalogItem,
  tier: 1 | 2 | 3 | 4 | null,
  unit?: CatalogItemUnit | null,
): boolean {
  if (tier == null || tier === 1) return false;
  return tierOf(unit ?? sellingUnitOf(item), tier) == null;
}
